// The job's labor read as the shop does it: every operation on the job as one line, whatever
// kinds of item its steps came from, with its one pooled setup; and those lines gathered by
// labor class or by station. A view of the resolution, never stored. The same for materials:
// one line per material across the job, so three groups' shares of a sheet read as the sheets.

import { Rat } from './rational.ts';
import type { ResolvedBom, ResolvedGroup, ResolvedStep } from './types.ts';

export interface OperationLine {
  operation_id: string;
  operation_name: string;
  labor_class_id: string | null;
  work_center_id: string | null;
  phase_id: string | null;
  unit: string | null;
  minutes_per_unit: string;
  /** The quantity across the job. */
  qty: string;
  /** The setup paid, once for the job (or none). */
  setup_minutes: string;
  minutes: string;
  hours: string;
  cents: number;
  /** How many kinds of item (groups) the quantity came from. */
  groups: number;
  verified: boolean;
  warnings: string[];
}

/** Every operation on the job as one line, in the order the steps first appear. */
export function laborByOperation(groups: Pick<ResolvedGroup, 'steps'>[]): OperationLine[] {
  const by = new Map<string, { line: OperationLine; qty: Rat; setup: Rat; minutes: Rat; sources: Set<number> }>();
  groups.forEach((g, gi) => {
    for (const s of g.steps as ResolvedStep[]) {
      let e = by.get(s.operation_id);
      if (!e) {
        e = {
          line: {
            operation_id: s.operation_id, operation_name: s.operation_name, labor_class_id: s.labor_class_id, work_center_id: s.work_center_id, phase_id: s.phase_id,
            unit: s.unit, minutes_per_unit: s.minutes_per_unit, qty: '0', setup_minutes: '0', minutes: '0', hours: '0', cents: 0, groups: 0, verified: s.verified, warnings: [],
          },
          qty: Rat.ZERO, setup: Rat.ZERO, minutes: Rat.ZERO, sources: new Set(),
        };
        by.set(s.operation_id, e);
      }
      e.qty = e.qty.add(Rat.parse(s.qty));
      // the ten-place figures where the step carries them: a share of a setup is rarely a round number, and the sums must be
      e.setup = e.setup.add(Rat.parse(s.setup_minutes_exact ?? s.setup_minutes));
      e.minutes = e.minutes.add(Rat.parse(s.minutes_exact ?? s.minutes));
      e.line.cents += s.cents;
      if (Rat.parse(s.qty).sign() > 0) e.sources.add(gi);
      for (const w of s.warnings) if (!e.line.warnings.includes(w)) e.line.warnings.push(w);
    }
  });
  return [...by.values()].map((e) => ({
    ...e.line,
    qty: e.qty.toDecimal(6),
    setup_minutes: e.setup.toDecimal(4),
    minutes: e.minutes.toDecimal(4),
    hours: e.minutes.div(Rat.of(60n)).toDecimal(4),
    groups: e.sources.size,
  }));
}

export interface LaborBucket<K extends string | null> {
  key: K;
  lines: OperationLine[];
  minutes: string;
  hours: string;
  cents: number;
}

/** The operation lines gathered by one of their fields (labor class, station, phase), in first-seen order. */
export function gatherLabor(lines: OperationLine[], field: 'labor_class_id' | 'work_center_id' | 'phase_id'): LaborBucket<string | null>[] {
  const by = new Map<string | null, { lines: OperationLine[]; minutes: Rat; cents: number }>();
  for (const l of lines) {
    const k = l[field];
    let b = by.get(k);
    if (!b) by.set(k, (b = { lines: [], minutes: Rat.ZERO, cents: 0 }));
    b.lines.push(l);
    b.minutes = b.minutes.add(Rat.parse(l.minutes));
    b.cents += l.cents;
  }
  return [...by].map(([key, b]) => ({ key, lines: b.lines, minutes: b.minutes.toDecimal(4), hours: b.minutes.div(Rat.of(60n)).toDecimal(4), cents: b.cents }));
}

export interface MaterialLine {
  material_id: string | null;
  description: string;
  unit: string | null;
  vendor: string | null;
  price: string;
  /** The quantity across the job: three groups' shares of a sheet read as the sheets. */
  qty: string;
  cents: number;
  groups: number;
  verified: boolean;
  typed_price: boolean;
  /** Separate pricing on every line of this material (2026-10-22), on some (mixed), or on none. */
  separate_pricing: 'all' | 'some' | 'none';
  /** Priced outside gross margin: on every line, on some (mixed), or on none. */
  outside_margin: 'all' | 'some' | 'none';
  /** The markup the lines are charged at, as text; 'mixed' when the lines differ. */
  markup_pct: string;
  /** What the lines charge before tax and before the margin, and the tax they pay, across the job. */
  charged_cents: number;
  tax_cents: number;
  /** Subcontracted work: the sub markup, no margin; counted with the subs, not the materials. */
  sub_work: boolean;
  warnings: string[];
  /** Where to buy it, typed on the items (an allowance line's vendor page), each with the item that typed it. */
  links: { item_id: string; number: string; url: string }[];
  /** The pack it is sold in, as the lines carry it (whole packs: the packs the job buys, the same on every line). */
  pack?: ResolvedBom['pack'];
}

/** Every material on the job as one line (a typed price by its name), in the order the lines first appear. */
export function materialsByMaterial(groups: Pick<ResolvedGroup, 'bom'>[]): MaterialLine[] {
  const by = new Map<string, { line: MaterialLine; qty: Rat; sources: Set<number>; sep: boolean[]; out: boolean[]; marks: Set<string> }>();
  groups.forEach((g, gi) => {
    for (const b of g.bom as ResolvedBom[]) {
      const key = b.material_id ?? `${b.material_role ?? ''}|${b.description}`;
      let e = by.get(key);
      if (!e) {
        e = {
          line: {
            material_id: b.material_id, description: b.description, unit: b.unit, vendor: b.vendor, price: b.price, qty: '0', cents: 0, groups: 0,
            verified: b.verified, typed_price: b.typed_price, separate_pricing: 'none', outside_margin: 'none', markup_pct: b.markup_pct, charged_cents: 0, tax_cents: 0, sub_work: !!b.sub_work, warnings: [], links: [],
            ...(b.pack ? { pack: b.pack } : {}),
          },
          qty: Rat.ZERO, sources: new Set(), sep: [], out: [], marks: new Set(),
        };
        by.set(key, e);
      }
      e.qty = e.qty.add(Rat.parse(b.qty));
      e.line.cents += b.cents;
      e.line.charged_cents += b.charged_cents ?? 0;
      e.line.tax_cents += b.tax_cents ?? 0;
      if (Rat.parse(b.qty).sign() > 0) e.sources.add(gi);
      e.sep.push(!!b.separate_pricing);
      e.out.push(!!b.outside_margin);
      e.marks.add(b.markup_pct);
      if (b.sub_work) e.line.sub_work = true;
      for (const k of b.links ?? []) if (!e.line.links.some((x) => x.item_id === k.item_id && x.url === k.url)) e.line.links.push(k);
      for (const w of b.warnings) if (!e.line.warnings.includes(w)) e.line.warnings.push(w);
    }
  });
  const allOrSome = (xs: boolean[]): 'all' | 'some' | 'none' => (xs.every(Boolean) ? 'all' : xs.some(Boolean) ? 'some' : 'none');
  return [...by.values()].map((e) => ({ ...e.line, qty: e.qty.toDecimal(6), groups: e.sources.size, separate_pricing: allOrSome(e.sep), outside_margin: allOrSome(e.out), markup_pct: e.marks.size > 1 ? 'mixed' : e.line.markup_pct }));
}
